import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";

// ─── Lazy-loaded routes ────────────────────────────────────────────────────────
const Home                = lazy(() => import("./pages/Home"));
const About               = lazy(() => import("./pages/About"));
const Events              = lazy(() => import("./pages/Events"));
const Contact             = lazy(() => import("./pages/Contact"));
const Donate              = lazy(() => import("./pages/Donate"));
const SignUp              = lazy(() => import("./pages/SignUp"));
const Admin               = lazy(() => import("./pages/Admin"));
const KnowMore            = lazy(() => import("./components/knowmore"));
const Needhelp            = lazy(() => import("./components/Needhelp"));
const TermsAndPrivacyPolicy = lazy(() => import("./components/TermsAndPrivacyPolicy"));
const AdminDashboard      = lazy(() => import("./pages/AdminDashboard"));
const Members             = lazy(() => import("./components/Members"));
const Collection          = lazy(() => import("./components/Collection"));
const Report              = lazy(() => import("./components/Report"));
const Verification        = lazy(() => import("./components/Verification"));
const Mails               = lazy(() => import("./components/Mails"));
const Theme               = lazy(() => import("./components/Theme"));
const Dashboard           = lazy(() => import("./components/Dashboard"));
const WAM                 = lazy(() => import("./components/WAM"));
const MemberAgreementForm = lazy(() => import("./components/MemberAgreementForm"));
const SadqaBoxForm        = lazy(() => import("./components/SadqaBoxForm"));
const VolunteerAgreementForm = lazy(() => import("./components/VolunteerAgreementForm"));
const Schedule            = lazy(() => import("./components/Schedule"));

// ─── Suspense fallback ─────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      flexDirection: "column",
      gap: "16px",
      fontFamily: "system-ui, sans-serif",
      background: "#f9f9f9",
    }}>
      <div style={{
        width: "40px",
        height: "40px",
        border: "4px solid #eee",
        borderTop: "4px solid #162f06",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <p style={{ color: "#162f06", fontWeight: 600, margin: 0, fontSize: "14px" }}>
        Loading...
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function App() {

  // ── Global autocomplete / autocorrect / spellcheck enabler ──────────────────
  // Applies browser-native text-assist features to every <input> and <textarea>
  // rendered anywhere in the app, including dynamically loaded components.
  useEffect(() => {
    const EXCLUDED_TYPES = new Set(["password", "file", "checkbox", "radio", "range", "color", "hidden", "submit", "reset", "button", "image"]);

    function applyAssist(el) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
      if (EXCLUDED_TYPES.has(el.type)) return;

      // Only set if not already explicitly configured
      if (!el.getAttribute("autocomplete")) {
        // Give semantic hints where possible
        const name = (el.name || el.id || "").toLowerCase();
        if      (name.includes("email"))   el.setAttribute("autocomplete", "email");
        else if (name.includes("phone") || name.includes("mobile") || name.includes("contact")) el.setAttribute("autocomplete", "tel");
        else if (name.includes("name"))    el.setAttribute("autocomplete", "name");
        else if (name.includes("address") || name.includes("addr")) el.setAttribute("autocomplete", "street-address");
        else if (name.includes("city"))    el.setAttribute("autocomplete", "address-level2");
        else if (name.includes("pin") || name.includes("zip") || name.includes("postal")) el.setAttribute("autocomplete", "postal-code");
        else                               el.setAttribute("autocomplete", "on");
      }

      // Autocorrect + autocapitalize (mobile browsers)
      if (!el.getAttribute("autocorrect"))   el.setAttribute("autocorrect", "on");
      if (!el.getAttribute("autocapitalize")) el.setAttribute("autocapitalize", "sentences");

      // Spellcheck on text areas and non-numeric inputs
      if (el instanceof HTMLTextAreaElement || el.type === "text" || el.type === "search") {
        el.spellcheck = true;
      }
    }

    // Patch all existing inputs on mount
    document.querySelectorAll("input, textarea").forEach(applyAssist);

    // Watch for dynamically rendered inputs (lazy routes, modals, etc.)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue; // element nodes only
          if (node.matches?.("input, textarea")) applyAssist(node);
          node.querySelectorAll?.("input, textarea").forEach(applyAssist);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route exact path="/"                      element={<Home />} />
            <Route exact path="/about"                 element={<About />} />
            <Route exact path="/event"                 element={<Events />} />
            <Route exact path="/TermsAndPrivacyPolicy" element={<TermsAndPrivacyPolicy />} />
            <Route exact path="/contact"               element={<Contact />} />
            <Route exact path="/donate"                element={<Donate />} />
            <Route exact path="/signup"                element={<SignUp />} />
            <Route exact path="/admin"                 element={<Admin />} />
            <Route exact path="/needhelp"              element={<Needhelp />} />
            <Route exact path="/wam"                   element={<WAM />} />
            <Route exact path="/new"                   element={<KnowMore />} />
            <Route path="/knowmore"                    element={<KnowMore />} />
            <Route path="/members"                     element={<Members />} />
            <Route path="/collection"                  element={<Collection />} />
            <Route path="/report"                      element={<Report />} />
            <Route path="/verification"                element={<Verification />} />
            <Route path="/mails"                       element={<Mails />} />
            <Route path="/theme"                       element={<Theme />} />
            <Route path="/dashboard"                   element={<Dashboard />} />
            <Route path="/member-agreement"            element={<MemberAgreementForm />} />
            <Route path="/sadqa-box"                   element={<SadqaBoxForm />} />
            <Route path="/volunteer-agreement"         element={<VolunteerAgreementForm />} />
            <Route path="/schedule"                    element={<Schedule />} />
            <Route
              path="/admindashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route exact path="*" element={<Navigate to={"/"} />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
